import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  ImageList,
  ImageListItem,
  useMediaQuery,
  useTheme,
  Button
} from '@mui/material';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import { motion } from 'framer-motion';
import { selectGalleryItems } from '../redux/gallerySlice';
import { useSelector } from 'react-redux';
import PageHeader from '../components/PageHeader';

function GalleryDetails() {
  const theme = useTheme();
  const { category } = useParams(); // Using 'category' as the folder ID from the route
  const galleryItems = useSelector(selectGalleryItems);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const selectedFolder = useMemo(() => {
    return (galleryItems || []).find(f => String(f.id) === String(category));
  }, [galleryItems, category]);

  const images = selectedFolder?.images || [];

  const getColumns = () => {
    if (isMobile) return 1;
    if (isMedium) return 2;
    return 3;
  };

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  if (!selectedFolder && galleryItems.length > 0) {
    return (
      <Container sx={{ py: 15, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Gallery not found</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.history.back()}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      <PageHeader
        showBackButton={true}
        title={selectedFolder?.title || "Event Gallery"}
        subtitle={selectedFolder?.description || 'Explore the captured moments of this event.'}
      // metadata={[
      //   { label: 'Asset Count', value: images.length },
      //   { label: 'Event Record', value: selectedFolder?.folderName }
      // ]}
      />

      <Container maxWidth="xl" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <ImageList
            variant="masonry"
            cols={getColumns()}
            gap={32}
            sx={{
              mb: 8,
              '& .MuiImageListItem-root': {
                overflow: 'hidden',
                borderRadius: '24px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                transition: 'all 0.5s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 20px 40px rgba(79, 70, 229, 0.15)',
                  '& img': { transform: 'scale(1.1)' },
                },
              },
            }}
          >
            {images.map((img, index) => (
              <ImageListItem
                sx={{ cursor: 'pointer', mb: 4 }}
                onClick={() => openLightbox(index)}
                key={img.id || index}
              >
                <motion.img
                  src={img.imageUrl}
                  alt={selectedFolder?.title}
                  loading="lazy"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  style={{ borderRadius: '24px', width: '100%', height: 'auto', display: 'block' }}
                />
              </ImageListItem>
            ))}
          </ImageList>

          <Lightbox
            open={isOpen}
            close={() => setIsOpen(false)}
            index={photoIndex}
            slides={images.map(img => ({
              src: img.imageUrl,
              title: selectedFolder?.title,
              description: selectedFolder?.description
            }))}
            plugins={[Zoom, Captions]}
            on={{ view: ({ index }) => setPhotoIndex(index) }}
            animation={{ zoom: 500 }}
            zoom={{ maxZoomPixelRatio: 3, zoomInMultiplier: 2 }}
            carousel={{ spacing: 0.5, padding: 0, imageFit: 'contain' }}
          />
        </motion.div>
      </Container>
    </Box>
  );
}

export default GalleryDetails;
